import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  listMyOrders, getOrderDelivery, getMarketplaceDownloadUrl, getMarketplaceAccess,
  formatPrice, categoryLabel,
} from '../services/marketplaceService';
import { toast } from './toastBus';

// Tarikh tamat akses. Order membawa Timestamp Firestore; callable pula
// memulangkan milisaat — kedua-duanya diterima di sini.
function tarikhPenuh(ts) {
  const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' });
}

function sudahTamat(ts) {
  const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
  return !!d && !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
}

// ═══════════════════════════════════════════════════════════
//  PEMBELIAN SAYA — buyer locker (re-access + download)
// ═══════════════════════════════════════════════════════════

const CAT_ICONS = { ebook: 'menu_book', tiktok_account: 'music_note', flow_ultra: 'bolt' };

const STATUS_LABEL = {
  awaiting_payment: { text: 'Menunggu pembayaran', icon: 'credit_card', bg: '#fffbeb', fg: '#b45309', border: '#fde68a' },
  requested: { text: 'Menunggu pengesahan', icon: 'hourglass_top', bg: '#fffbeb', fg: '#b45309', border: '#fde68a' },
  delivered: { text: 'Siap', icon: 'check_circle', bg: '#ecfdf5', fg: '#047857', border: '#a7f3d0' },
  rejected: { text: 'Ditolak', icon: 'cancel', bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca' },
  refunded: { text: 'Dikembalikan', icon: 'undo', bg: '#f3f4f6', fg: '#374151', border: '#e5e7eb' },
};

export default function MyPurchasesView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderPulang = searchParams.get('order'); // pulang daripada Billplz
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deliveries, setDeliveries] = useState({}); // orderId -> info text
  const [downloading, setDownloading] = useState(null);
  const [access, setAccess] = useState({});         // orderId -> { content, expiresAt, expired }
  const [loadingAccess, setLoadingAccess] = useState(null);

  useEffect(() => { load(); }, []);

  // Pulang daripada Billplz: webhook mungkin belum sempat mendarat (biasanya
  // beberapa saat). Tanpa semakan berulang, pelanggan yang baru sahaja bayar
  // melihat "Menunggu pembayaran" dan menyangka duitnya hilang.
  useEffect(() => {
    if (!orderPulang) return undefined;
    let cubaan = 0;
    const jam = setInterval(async () => {
      cubaan += 1;
      const list = await load(true);
      const o = list?.find((x) => x.id === orderPulang);
      if (!o || o.status !== 'awaiting_payment' || cubaan >= 10) clearInterval(jam);
    }, 3000);
    return () => clearInterval(jam);
  }, [orderPulang]);

  async function load(senyap = false) {
    if (!senyap) setLoading(true);
    try {
      const list = await listMyOrders();
      setOrders(list);
      // Eager-load delivery info for delivered manual (non-ebook) orders.
      // Pesanan auto-serah dikecualikan — kredentialnya tidak duduk dalam doc ini,
      // dan ia hanya diambil bila pelanggan menekan "Lihat Akses".
      const manual = list.filter((o) => o.status === 'delivered'
        && o.category !== 'ebook' && o.fulfillment !== 'auto_stock');
      const map = {};
      await Promise.all(manual.map(async (o) => {
        try {
          const d = await getOrderDelivery(o.id);
          if (d?.info) map[o.id] = d.info;
        } catch { /* ignore — rules may block if not owner */ }
      }));
      setDeliveries(map);
      return list;
    } catch (e) {
      // Semakan senyap tidak menjerit — toast berulang setiap 3 saat
      // lebih mengganggu daripada membantu.
      if (!senyap) toast.error('Gagal muat pembelian: ' + (e?.message || e));
      return null;
    } finally {
      if (!senyap) setLoading(false);
    }
  }

  async function handleDownload(o) {
    setDownloading(o.id);
    try {
      const { url } = await getMarketplaceDownloadUrl(o.id);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Gagal muat turun: ' + (e?.message || e));
    } finally {
      setDownloading(null);
    }
  }

  // Kredential diminta daripada server pada klik — bukan dimuat awal.
  // Server yang menyemak pemilik pesanan dan tempoh akses.
  async function handleLihatAkses(o) {
    setLoadingAccess(o.id);
    try {
      const res = await getMarketplaceAccess(o.id);
      setAccess((m) => ({ ...m, [o.id]: res }));
    } catch (e) {
      toast.error('Gagal dapatkan akses: ' + (e?.message || e));
    } finally {
      setLoadingAccess(null);
    }
  }

  async function handleCopy(text) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Info akses disalin!');
    } catch {
      toast.error('Gagal salin — sila salin secara manual.');
    }
  }

  return (
    <div style={S.wrap}>
      <div className="sesaat-m-tight" style={S.page}>
        <div style={S.headerRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={S.headerIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 24, color: '#fff' }}>receipt_long</span>
            </div>
            <div>
              <h1 style={S.h1}>Pembelian Saya</h1>
              <p style={S.sub}>Akses semula barang yang anda beli — bila-bila masa.</p>
            </div>
          </div>
          <button className="mkl-ghost" style={S.ghost} onClick={() => navigate('/marketplace')}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>storefront</span> Marketplace
          </button>
        </div>

        {loading ? (
          <div style={S.muted}>Memuatkan…</div>
        ) : orders.length === 0 ? (
          <div style={S.empty}>
            <span className="material-symbols-outlined" style={{ fontSize: 48, color: '#d1d5db' }}>shopping_bag</span>
            <div style={{ fontWeight: 700, color: '#374151' }}>Belum ada pembelian</div>
            <div style={{ fontSize: 13 }}>Lawati Marketplace untuk mula membeli.</div>
            <button className="mkl-cta" style={S.cta} onClick={() => navigate('/marketplace')}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>storefront</span> Ke Marketplace
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {orders.map((o) => {
              const st = STATUS_LABEL[o.status] || STATUS_LABEL.requested;
              const isEbook = o.category === 'ebook';
              const delivered = o.status === 'delivered';
              const isAutoStock = o.fulfillment === 'auto_stock';
              const akses = access[o.id];
              // Tarikh daripada callable lebih dipercayai — ia dibaca daripada
              // akaun itu sendiri, jadi ia menunjukkan sambungan terkini.
              const tamatPada = akses?.expiresAt ?? o.accessExpiresAt ?? null;
              const tamat = akses ? !!akses.expired : sudahTamat(tamatPada);
              return (
                <div key={o.id} style={S.card}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <div style={S.catAvatar}>
                      <span className="material-symbols-outlined" style={{ fontSize: 22, color: '#4f46e5' }}>
                        {CAT_ICONS[o.category] || 'storefront'}
                      </span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <div>
                          <div style={S.title}>{o.productTitle}</div>
                          <div style={S.meta}>{categoryLabel(o.category)} · {o.amount ? formatPrice(o.amount) : 'Percuma'}</div>
                        </div>
                        <span style={{ ...S.pill, background: st.bg, color: st.fg, border: `1px solid ${st.border}` }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{st.icon}</span>
                          {st.text}
                        </span>
                      </div>

                      {delivered && isEbook && (
                        <button
                          className="mkl-cta"
                          style={{ ...S.cta, marginTop: 12, opacity: downloading === o.id ? 0.6 : 1 }}
                          disabled={downloading === o.id}
                          onClick={() => handleDownload(o)}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>download</span>
                          {downloading === o.id ? 'Menyediakan…' : 'Muat Turun Ebook'}
                        </button>
                      )}

                      {/* ─── Akaun auto-serah ─── */}
                      {delivered && isAutoStock && (
                        <div style={S.deliveryBox}>
                          <div style={S.deliveryHead}>
                            <span style={S.deliveryLabel}>
                              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>key</span>
                              Akses Akaun
                            </span>
                            {akses?.content && (
                              <button className="mkl-copy" style={S.copyBtn} onClick={() => handleCopy(akses.content)}>
                                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>content_copy</span> Salin
                              </button>
                            )}
                          </div>

                          {tamatPada && (
                            <div style={{ ...S.meta, marginTop: 0, marginBottom: 10 }}>
                              {tamat
                                ? `Tempoh akses tamat pada ${tarikhPenuh(tamatPada)}.`
                                : `Aktif sehingga ${tarikhPenuh(tamatPada)}.`}
                            </div>
                          )}

                          {tamat ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
                              <div style={{ ...S.meta, marginTop: 0 }}>
                                Tempoh akses anda telah tamat. Sila beli semula untuk menyambung —
                                anda akan menerima akaun yang sama.
                              </div>
                              <button className="mkl-cta" style={S.cta}
                                onClick={() => navigate(`/marketplace/p/${o.productId}`)}>
                                Sambung Langganan
                              </button>
                            </div>
                          ) : akses?.content ? (
                            <pre style={S.deliveryText}>{akses.content}</pre>
                          ) : (
                            <button
                              className="mkl-cta"
                              style={{ ...S.cta, opacity: loadingAccess === o.id ? 0.6 : 1 }}
                              disabled={loadingAccess === o.id}
                              onClick={() => handleLihatAkses(o)}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>key</span>
                              {loadingAccess === o.id ? 'Membuka…' : 'Lihat Akses'}
                            </button>
                          )}
                        </div>
                      )}

                      {delivered && !isEbook && !isAutoStock && (
                        <div style={S.deliveryBox}>
                          <div style={S.deliveryHead}>
                            <span style={S.deliveryLabel}>
                              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>key</span>
                              Info Akses
                            </span>
                            {deliveries[o.id] && (
                              <button className="mkl-copy" style={S.copyBtn} onClick={() => handleCopy(deliveries[o.id])}>
                                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>content_copy</span> Salin
                              </button>
                            )}
                          </div>
                          {deliveries[o.id] ? (
                            <pre style={S.deliveryText}>{deliveries[o.id]}</pre>
                          ) : (
                            <div style={S.meta}>Tiada info dipaparkan. Hubungi admin jika perlu.</div>
                          )}
                        </div>
                      )}

                      {o.status === 'awaiting_payment' && (
                        <div>
                          <div style={S.waiting}>
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>schedule</span>
                            {o.id === orderPulang
                              ? 'Pembayaran anda sedang disahkan — biasanya beberapa saat sahaja.'
                              : 'Pembayaran belum selesai. Anda boleh menyambungnya semula.'}
                          </div>
                          {o.billplzUrl && (
                            <button className="mkl-cta" style={{ ...S.cta, marginTop: 10 }}
                              onClick={() => { window.location.href = o.billplzUrl; }}>
                              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>credit_card</span>
                              Bayar Sekarang
                            </button>
                          )}
                        </div>
                      )}

                      {o.status === 'requested' && (
                        <div style={S.waiting}>
                          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>schedule</span>
                          Pesanan diterima. Admin akan sahkan bayaran &amp; serahkan akses tidak lama lagi.
                        </div>
                      )}
                      {o.status === 'rejected' && o.rejectReason && (
                        <div style={{ ...S.meta, marginTop: 8 }}>Sebab: {o.rejectReason}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        .mkl-ghost:hover { border-color: #a5b4fc !important; color: #4f46e5 !important; }
        .mkl-cta { transition: transform .15s ease, box-shadow .15s ease; }
        .mkl-cta:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(79,70,229,.35); }
        .mkl-copy:hover { background: #eef2ff !important; color: #4f46e5 !important; }
      `}</style>
    </div>
  );
}

const S = {
  wrap: { height: '100%', overflowY: 'auto', background: '#f8fafc' },
  page: { padding: '28px 32px 48px', maxWidth: 820, margin: '0 auto' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 22, flexWrap: 'wrap' },
  headerIcon: { width: 46, height: 46, borderRadius: 13, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 16px rgba(99,102,241,.3)', flexShrink: 0 },
  h1: { fontSize: 24, fontWeight: 800, margin: 0, color: '#111827', letterSpacing: '-0.02em' },
  sub: { margin: '2px 0 0', color: '#6b7280', fontSize: 13.5 },
  ghost: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  card: { border: '1px solid #e8eaf0', borderRadius: 16, padding: 18, background: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,.05)' },
  catAvatar: { width: 44, height: 44, borderRadius: 12, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontWeight: 700, fontSize: 15, color: '#111827', lineHeight: 1.35 },
  meta: { fontSize: 12.5, color: '#6b7280', marginTop: 3 },
  pill: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 800, padding: '5px 12px', borderRadius: 999, height: 'fit-content' },
  cta: { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #4f46e5, #6d28d9)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(79,70,229,.3)' },
  deliveryBox: { marginTop: 12, padding: 14, border: '1px solid #e8eaf0', borderRadius: 12, background: '#f8fafc' },
  deliveryHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  deliveryLabel: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' },
  copyBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 700, fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit' },
  deliveryText: { margin: 0, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 },
  waiting: { display: 'flex', alignItems: 'center', gap: 7, marginTop: 12, fontSize: 12.5, fontWeight: 600, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '9px 12px' },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#9ca3af', fontSize: 14, padding: '70px 0', textAlign: 'center' },
  muted: { color: '#9ca3af', fontSize: 14, padding: '24px 0' },
};
